import {
  Box,
  Button,
  FormControl,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { useCallback, useEffect, useState } from "react";
import {
  Controller,
  ControllerRenderProps,
  FieldError,
  useFormContext,
} from "react-hook-form";
import {
  TemplateVariable,
  useTemplateHydration,
} from "../../hooks/useTemplateHydration";
import { CommunicationFormType } from "../../types";
import CustomEditor from "./CustomEditor";
import PreviewPane from "./PreviewPane";
import { VariablesMenu } from "./VariablesMenu";

// Simple utility to hydrate a template with {{variable}} placeholders
export function hydrateTemplate(
  template: string,
  variables: TemplateVariable[]
): string {
  if (!template) return template;
  return template.replace(
    /\{\{(\w+)\}\}/g,
    (_, v) => variables.find((variable) => variable.name === v)?.value || ""
  );
}

export type EmailBodyEditorMode = "edit" | "preview" | "split";

interface EmailBodyEditorProps {
  index: number;
  disabled?: boolean;
  error?: FieldError;
}

export const EmailBodyEditor = ({
  index,
  disabled = false,
  error = undefined,
}: EmailBodyEditorProps) => {
  const { control } = useFormContext<CommunicationFormType>();

  return (
    <FormControl fullWidth>
      <Controller
        name={`recipients.${index}.emailBodyEnriched`}
        control={control}
        render={({ field }) => (
          <ControlledEditor field={field} disabled={disabled} error={error} />
        )}
      />
    </FormControl>
  );
};

const ControlledEditor = ({
  field,
  disabled,
  error,
}: {
  field: ControllerRenderProps<
    CommunicationFormType,
    `recipients.${number}.emailBodyEnriched`
  >;
  disabled: boolean;
  error?: FieldError;
}) => {
  // Editor state
  const [mode, setMode] = useState<EmailBodyEditorMode>("edit");
  const [variablesMenuAnchor, setVariablesMenuAnchor] =
    useState<HTMLElement | null>(null);

  // Use the new simplified hydration hook
  const { templateVariables } = useTemplateHydration();
  // Defensive: handle undefined or malformed value
  const value = field.value || { htmlTemplate: "", htmlHydrated: "" };
  const { htmlTemplate, htmlHydrated } = value;

  // Hydrate the template when the template variables change
  useEffect(() => {
    const hydrated = hydrateTemplate(htmlTemplate, templateVariables);
    if (htmlHydrated !== hydrated) {
      field.onChange({
        htmlTemplate: htmlTemplate,
        htmlHydrated: hydrated,
      });
    }
  }, [templateVariables, field, field.onChange, htmlTemplate, htmlHydrated]);

  // When editing, update only htmlTemplate and keep htmlHydrated as is (or clear it)
  const handleTemplateChange = useCallback(
    (val: string) => {
      const hydrated = hydrateTemplate(val, templateVariables);
      field.onChange({
        htmlTemplate: val,
        htmlHydrated: hydrated,
      });
    },
    [templateVariables, field]
  );

  return (
    <Stack spacing={2} sx={{ mr: 9 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, v) => v && setMode(v)}
            size="small"
            aria-label="editor mode"
            sx={{
              "& .Mui-selected": {
                backgroundColor: "#517BC614 !important",
                color: "#517BC6 !important",
              },
            }}
          >
            <ToggleButton value="edit" aria-label="edit mode">
              Edit
            </ToggleButton>
            <ToggleButton value="preview" aria-label="preview mode">
              Preview
            </ToggleButton>
            <ToggleButton value="split" aria-label="split mode">
              Split
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            size="small"
            sx={{ color: "grey.400" }}
            onClick={(e) => e.preventDefault()}
          >
            <InfoOutlined fontSize="small" />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                left: "100%",
                ml: 1,
                width: 300,
                height: 90,
                whiteSpace: "normal",
                bgcolor: "background.paper",
                color: "text.primary",
                p: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                display: "none",
                ".MuiIconButton-root:hover &": {
                  display: "block",
                },
              }}
            >
              Merge fields will be automatically populated with selected
              facilities and tenant name. The preview shows how this email will
              appear to the recipient.
            </Typography>
          </IconButton>
        </Stack>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={(e) => setVariablesMenuAnchor(e.currentTarget)}
          disabled={disabled || mode === "preview"}
          sx={{
            pr: 0,
            alignSelf: "flex-end",
          }}
        >
          Copy Variable
        </Button>
      </Stack>
      <Box sx={{ minHeight: 300 }}>
        <ToggleableEditorGroup
          mode={mode}
          htmlTemplate={htmlTemplate}
          htmlHydrated={htmlHydrated}
          label="Email Body"
          handleTemplateChange={handleTemplateChange}
        />
      </Box>
      <ErrorDisplay error={error} />
      <VariablesMenu
        anchorEl={variablesMenuAnchor}
        open={Boolean(variablesMenuAnchor)}
        onClose={() => setVariablesMenuAnchor(null)}
        variables={templateVariables}
      />
    </Stack>
  );
};

const ToggleableEditorGroup = ({
  mode,
  htmlTemplate,
  htmlHydrated,
  label,
  handleTemplateChange,
}: {
  mode: EmailBodyEditorMode;
  htmlTemplate: string;
  htmlHydrated: string;
  label: string;
  handleTemplateChange: (val: string) => void;
}) => {
  return (
    <Stack direction="row" spacing={2} sx={{ minHeight: 300 }}>
      {(mode === "edit" || mode === "split") && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CustomEditor
            html={htmlTemplate}
            onChange={handleTemplateChange}
            label={label}
          />
        </Box>
      )}
      {(mode === "preview" || mode === "split") && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PreviewPane html={htmlHydrated} label={label} />
        </Box>
      )}
    </Stack>
  );
};
